import { ConfigServiceProvider } from '../../../core/services/config-service/config-service';
import { DbEntry } from '../../../core/model/db-entry.model';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, tap } from 'rxjs/operators';
import { News, NewsAttrs } from '../../model/news.model';
import { NewsComment } from '../../model/news-comment.model';
import { NewsCreateDTO } from '../../model/news-create-dto.model';
import { NewsWithComment } from '../../model/news-with-comment.model';
import { Observable } from 'rxjs/Observable';
import { Page } from '../../../core/model/page.model';
import { StorageServiceProvider } from '../../../core/services/storage-service/storage-service';
import { TimeUtils } from '../../../../utils/time.utils';

@Injectable()
export class NewsService {
  constructor(private http: HttpClient, private configService: ConfigServiceProvider, private storageService: StorageServiceProvider) {}

  public getNews(): Observable<News[]> {
    return this.load();
  }

  public load(): Observable<News[]> {
    return this.http.get<Page<News>>(this.configService.getUrl('news')).pipe(
      map((newsPage) => newsPage.content),
      tap((news) => this.saveNewsToStorage(JSON.stringify(news))),
      catchError(() => this.handleError()),
    );
  }

  private saveNewsToStorage(newsAttrsString: string): Promise<DbEntry> {
    const dbEntry: DbEntry = {
      lastModTime: TimeUtils.getUnixTimestamp(),
      value: newsAttrsString,
    };
    return this.storageService.setObject('news', dbEntry);
  }

  private handleError(): Observable<News[]> {
    console.error('Unable to retrieve current news from the server. Attempting to load saved ones');
    return Observable.from(this.storageService.get('news') as Promise<DbEntry>).pipe(
      map((dbEntry) => JSON.parse(dbEntry.value)),
      catchError(() => {
        console.error('Unable to retrieve saved news from the local storage. Empty array will be return');
        return Observable.of([]);
      }),
    );
  }

  public getPage(page: number, size: number): Observable<Page<News>> {
    const params = new HttpParams().append('page', page.toString()).append('size', size.toString());
    return this.http.get<Page<News>>(this.configService.getUrl('news'), { params });
  }

  public like(id: number): Observable<void> {
    return this.http.post<void>(this.configService.getUrl('news') + '/' + id + '/like', {});
  }

  public unlike(id: number): Observable<void> {
    return this.http.delete<void>(this.configService.getUrl('news') + '/' + id + '/like');
  }

  public comment(id: number, text: string): Observable<void> {
    const requestData = new HttpParams().set('comment', text);
    const httpOptions = {
      headers: new HttpHeaders().set('Content-type', 'application/x-www-form-urlencoded; charset=utf-8'),
    };

    return this.http.post<void>(this.configService.getUrl('news') + '/' + id + '/comment', requestData, httpOptions);
  }

  public deleteComment(newsId: number, commentId: number): Observable<void> {
    return this.http.get<void>(this.configService.getUrl('news') + '/' + newsId + '/comment/' + commentId + '/delete');
  }

  public getComments(id: number): Observable<Page<NewsComment>> {
    return this.http.get<Page<NewsComment>>(this.configService.getUrl('news') + '/' + id + '/comments');
  }

  public sendQuickNews(title: string, content: string): Observable<void> {
    const requestData = new HttpParams().set('title', title).set('content', content);
    const httpOptions = {
      headers: new HttpHeaders().set('Content-type', 'application/x-www-form-urlencoded; charset=utf-8'),
    };

    return this.http.post<void>(this.configService.getUrl('newsQuick'), requestData, httpOptions);
  }

  public sendNews(news: NewsCreateDTO): Observable<void> {
    let formData = new FormData();
    let requestParams;

    requestParams = new HttpParams().set('title', news.title).set('content', news.content).set('likeable', 'true').set('commentable', 'true');

    if (news.images.length > 0) {
      formData.set('image', news.images[0].file);
    }
    news.files.forEach((file) => formData.append('files', file.file, file.name));

    if (news.groups && news.groups.length > 0) {
      requestParams = requestParams.set('groups', JSON.stringify(news.groups.map((group) => [group])));
    }

    return this.http.post<void>(this.configService.getUrl('news'), formData, {
      params: requestParams,
    });
  }
}



// WEBPACK FOOTER //
// ./src/modules/news/services/news-service/news-service.ts